import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {NzRateModule} from 'ng-zorro-antd/rate';
import {Subscription} from 'rxjs';
import {PropertyService, PropertyRatingApi, PropertyRatingDetailApi} from '../services/property.service';
import {FormsModule} from '@angular/forms';

@Component({
  selector: 'app-rating-overview',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzRateModule, FormsModule],
  templateUrl: './rating-overview.component.html',
  styleUrl: './rating-overview.component.less'
})
export class RatingOverviewComponent implements OnInit, OnDestroy {
  loading = false;
  errorKey?: string;
  ratings: PropertyRatingApi[] = [];
  filteredRatings: PropertyRatingApi[] = [];
  searchTerm = '';

  readonly detailBatchSize = 5;

  readonly ratingCategories: Array<{ key: string; translationKey: string }> = [
    { key: 'cleanliness', translationKey: 'CLEANLINESS' },
    { key: 'comfort', translationKey: 'COMFORT' },
    { key: 'communication', translationKey: 'COMMUNICATION' },
    { key: 'overall', translationKey: 'OVERALL' },
  ];

  private subscription?: Subscription;
  private expandedPropertyIds = new Set<string>();
  private detailVisibleCount: Record<string, number> = {};

  constructor(private propertyService: PropertyService) {}

  ngOnInit(): void {
    this.loadRatings();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private loadRatings(): void {
    this.loading = true;
    this.errorKey = undefined;

    this.subscription = this.propertyService.getRatings().subscribe({
      next: (ratings: PropertyRatingApi[]) => {
        // No remapping: keep BE shape as-is
        this.ratings = ratings || [];
        this.syncDetailStates();
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load hospitality rating overview', error);
        this.errorKey = 'RATING_OVERVIEW.ERROR_LOAD';
        this.loading = false;
      },
    });
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.applyFilters();
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    this.applyFilters();
  }

  isFilteringActive(): boolean {
    return !!this.searchTerm.trim();
  }

  trackByProperty(_: number, rating: PropertyRatingApi): string {
    return rating.propertyId;
  }

  trackByDetail(index: number, detail: PropertyRatingDetailApi): string {
    return detail.submittedAt || `detail-${index}`;
  }

  togglePropertyDetails(rating: PropertyRatingApi): void {
    if (this.isExpanded(rating.propertyId)) {
      this.expandedPropertyIds.delete(rating.propertyId);
      return;
    }

    this.expandedPropertyIds.add(rating.propertyId);
    this.ensureDetailCount(rating);
  }

  isExpanded(propertyId: string): boolean {
    return this.expandedPropertyIds.has(propertyId);
  }

  getVisibleDetails(rating: PropertyRatingApi): PropertyRatingDetailApi[] {
    if (!rating.ratings?.length) {
      return [];
    }

    const count = this.detailVisibleCount[rating.propertyId] ?? this.detailBatchSize;
    return rating.ratings.slice(0, count);
  }

  getCategoryAverage(rating: PropertyRatingApi, key: string): number {
    const averages = rating.summary?.averages as Record<string, unknown> | undefined;
    if (!averages) {
      return 0;
    }
    const value = averages[key];
    return typeof value === 'number' ? value : 0;
  }

  hasMoreDetails(rating: PropertyRatingApi): boolean {
    if (!rating.ratings?.length) {
      return false;
    }
    const visible = this.detailVisibleCount[rating.propertyId] ?? this.detailBatchSize;
    return rating.ratings.length > visible;
  }

  canShowLess(rating: PropertyRatingApi): boolean {
    if (!rating.ratings?.length) {
      return false;
    }
    const visible = this.detailVisibleCount[rating.propertyId] ?? this.detailBatchSize;
    return rating.ratings.length > this.detailBatchSize && visible > this.detailBatchSize;
  }

  showMoreDetails(rating: PropertyRatingApi): void {
    if (!rating.ratings?.length) {
      return;
    }

    const current = this.detailVisibleCount[rating.propertyId] ?? this.detailBatchSize;
    const updated = Math.min(current + this.detailBatchSize, rating.ratings.length);
    this.detailVisibleCount[rating.propertyId] = updated;
  }

  showLessDetails(rating: PropertyRatingApi): void {
    this.detailVisibleCount[rating.propertyId] = this.detailBatchSize;
  }

  getAdditionalDetailsCount(rating: PropertyRatingApi): number {
    if (!rating.ratings?.length) {
      return 0;
    }
    const visible = this.detailVisibleCount[rating.propertyId] ?? this.detailBatchSize;
    return Math.max(rating.ratings.length - visible, 0);
  }

  getNextBatchCount(rating: PropertyRatingApi): number {
    const remaining = this.getAdditionalDetailsCount(rating);
    if (!remaining) {
      return 0;
    }
    return Math.min(remaining, this.detailBatchSize);
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredRatings = [...this.ratings];
      return;
    }

    this.filteredRatings = this.ratings.filter((rating) =>
      rating.propertyAddress?.toLowerCase().includes(term)
    );
  }

  private syncDetailStates(): void {
    const propertyIds = new Set(this.ratings.map((rating) => rating.propertyId));

    // Remove state for properties that no longer exist
    Array.from(this.expandedPropertyIds).forEach((propertyId) => {
      if (!propertyIds.has(propertyId)) {
        this.expandedPropertyIds.delete(propertyId);
      }
    });

    Object.keys(this.detailVisibleCount).forEach((propertyId) => {
      if (!propertyIds.has(propertyId)) {
        delete this.detailVisibleCount[propertyId];
      }
    });

    this.ratings.forEach((rating) => this.ensureDetailCount(rating));
  }

  private ensureDetailCount(rating: PropertyRatingApi): void {
    const total = rating.ratings?.length ?? 0;
    const current = this.detailVisibleCount[rating.propertyId];

    if (total === 0) {
      this.detailVisibleCount[rating.propertyId] = this.detailBatchSize;
      return;
    }

    if (current === undefined) {
      this.detailVisibleCount[rating.propertyId] = Math.min(this.detailBatchSize, total);
      return;
    }

    this.detailVisibleCount[rating.propertyId] = Math.min(
      Math.max(current, this.detailBatchSize),
      total
    );
  }
}
